import { useState } from 'react';
import { Button } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

interface RemoveElementButtonProps {
  /** Fired on the SECOND (confirming) click. */
  onRemove: () => void;
  disabled?: boolean;
}

/**
 * Danger-styled, two-step "Remove" button for the element edit dialogs: the first click arms it
 * ("Confirm remove?"), the second fires `onRemove`. This prevents an accidental one-click removal
 * without needing a separate confirmation modal.
 *
 * The confirming state resets naturally because the host dialogs unmount their content on close,
 * so each reopen mounts a fresh button.
 */
export function RemoveElementButton({ onRemove, disabled }: RemoveElementButtonProps) {
  const { t } = useTranslation('common');

  const [confirming, setConfirming] = useState(false);

  function handleClick() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onRemove();
  }

  return (
    <Button variant="destructive" onClick={handleClick} disabled={disabled}>
      {confirming
        ? t('components.removeElementButton.confirmRemove')
        : t('components.removeElementButton.remove')}
    </Button>
  );
}
